import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { sendOnWhatsApp, buildGalleryMessage } from '../../utils/sendOnWhatsApp';

const API = '/api/superadmin';

// Public-facing share base:
//   dev  → set REACT_APP_SHARE_BASE=http://YOUR_IP:3001/em in .env.development
//   prod → REACT_APP_SHARE_BASE=https://anuplights.com/em  in .env.production
const SHARE_BASE  = process.env.REACT_APP_SHARE_BASE || process.env.REACT_APP_API_URL || (window.location.origin + '/em');
const IS_LOCALHOST = SHARE_BASE.includes('localhost') || SHARE_BASE.includes('127.0.0.1');
// Direct photo URL base: https://anuplights.com/event-photos/{eventId}/{fileName}
const PHOTO_BASE  = process.env.REACT_APP_PHOTO_BASE || '';
// _SMP4687.JPG.jpg → _SMP4687.jpg | OVI00410.JPG → OVI00410.jpg
const lowerExt = (name) => {
  if (!name) return name;
  return name
    .replace(/\.[A-Z][A-Za-z0-9]*(\.[a-z][a-z0-9]*)$/, '$1') // .JPG.jpg → .jpg
    .replace(/\.[^/.]+$/, e => e.toLowerCase());               // .JPG → .jpg
};

const FIELDS = [
  ['Surname',        'surname'],
  ['Name',           'name'],
  ['Mobile',         'mobile_number', 'tel'],
  ['Email',          'email',         'email'],
  ['Password',       'password',      'password'],
  ['Father/Husband', 'father_husband'],
  ['Date of Birth',  'dob',           'date'],
  ['Address 1',      'address1'],
  ['Address 2',      'address2'],
  ['Pincode',        'pincode'],
  ['District',       'district'],
  ['State',          'state'],
  ['Spouse Name',    'spounce_name'],
  ['Spouse Mobile',  'spounce_mobile', 'tel'],
  ['Referred By',    'refered_by'],
  ['ISD',            'isd'],
];

const COLORS = ['bg-blue-700','bg-purple-700','bg-green-700','bg-amber-700','bg-rose-700','bg-teal-700','bg-indigo-700'];
const copyText = txt => navigator.clipboard?.writeText(txt).catch(() => {});

// ── Selfie-or-Avatar component ────────────────────────────────────────────────
const SelfieOrAvatar = ({ userId, name, colorClass, size = 'w-8 h-8', bust = '' }) => {
  const [failed, setFailed] = useState(false);
  const ini = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

  useEffect(() => { setFailed(false); }, [userId, bust]);

  if (failed) {
    return (
      <div className={`${size} rounded-full flex items-center justify-center font-bold text-[10px] text-white flex-shrink-0 ${colorClass}`}>
        {ini}
      </div>
    );
  }
  return (
    <div className={`${size} rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-sm`}>
      <img
        src={`/api/photos/selfie/${userId}${bust ? `?t=${bust}` : ''}`}
        alt={name}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────
const FilterStep = ({ num, label, children }) => (
  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
    <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">{num}. {label}</span>
    {children}
  </div>
);

const Arrow = () => <span className="text-slate-300 text-lg pb-0.5 flex-shrink-0">›</span>;

const IdChip = ({ label, value, warn }) => (
  <span className={`text-[9px] font-mono ${warn ? 'text-amber-600 font-bold' : 'text-slate-500'}`}>
    <span className="text-slate-400">{label}:</span>{' '}
    <span className={warn ? 'text-amber-600' : 'text-slate-700 font-semibold'}>{value ?? '—'}</span>
  </span>
);

// ── Main component ────────────────────────────────────────────────────────────
const SuperAdminVisitorsMgmtPage = () => {
  // Filter
  const [dealers,        setDealers]        = useState([]);
  const [dealerId,       setDealerId]       = useState('');
  const [photographers,  setPhotographers]  = useState([]);
  const [photogId,       setPhotogId]       = useState('');
  const [events,         setEvents]         = useState([]);
  const [eventId,        setEventId]        = useState('');
  // Visitors
  const [visitors,       setVisitors]       = useState([]);
  const [selected,       setSelected]       = useState(null);
  const [form,           setForm]           = useState(null);
  const [rightView,      setRightView]      = useState(null); // null | 'photos' | 'edit'
  // Photos
  const [photos,         setPhotos]         = useState([]);
  const [loadingPhotos,  setLoadingPhotos]  = useState(false);
  const [photosError,    setPhotosError]    = useState('');
  const [photosSource,   setPhotosSource]   = useState(''); // 'gallery' | 'admin' | ''
  // Selfie
  const [selfieFile,     setSelfieFile]     = useState(null);
  const [selfieMsg,      setSelfieMsg]      = useState('');
  const [uploadingSelfie,setUploadingSelfie]= useState(false);
  const [selfieBust,     setSelfieBust]     = useState({}); // {userId: timestamp}
  // Gallery
  const [generatingLink, setGeneratingLink] = useState(false);
  // WhatsApp
  const [waSending,      setWaSending]      = useState(''); // '' | 'gallery' | 'event'
  const [waResult,       setWaResult]       = useState(''); // '' | 'ok' | 'fail'
  // Loading
  const [saving,         setSaving]         = useState(false);
  const [loadingP,       setLoadingP]       = useState(false);
  const [loadingE,       setLoadingE]       = useState(false);
  const [loadingVis,     setLoadingVis]     = useState(false);
  // UI
  const [msg,            setMsg]            = useState('');
  const [search,         setSearch]         = useState('');
  const [copied,         setCopied]         = useState('');

  const token   = localStorage.getItem('ep_token');
  const headers = { Authorization: `Bearer ${token}` };

  const saveRef    = useRef();
  const escRef     = useRef();
  const selfieRef  = useRef();

  // ── Load dealers ────────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/users/dealers`, { headers })
      .then(r => setDealers(r.data)).catch(() => {});
  }, []);

  // ── Escape key ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') escRef.current?.(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  // ── Ctrl+S ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const h = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); saveRef.current?.click(); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  // ── Loaders ─────────────────────────────────────────────────────────────────
  const loadPhotographers = useCallback(async dId => {
    setLoadingP(true);
    try {
      const { data } = await axios.get(`${API}/users/photographers?dealer_id=${dId}`, { headers });
      setPhotographers(data);
    } catch { setPhotographers([]); }
    setLoadingP(false);
  }, []);

  const loadEvents = useCallback(async pId => {
    setLoadingE(true);
    try {
      const { data } = await axios.get(`${API}/photographers/${pId}/events`, { headers });
      setEvents(data);
    } catch { setEvents([]); }
    setLoadingE(false);
  }, []);

  const loadVisitors = useCallback(async eId => {
    setLoadingVis(true);
    try {
      const { data } = await axios.get(`${API}/events/${eId}/visitors`, { headers });
      setVisitors(data);
    } catch { setVisitors([]); }
    setLoadingVis(false);
  }, []);

  const loadPhotos = useCallback(async (eumId, galleryToken) => {
    setLoadingPhotos(true); setPhotos([]); setPhotosError(''); setPhotosSource('');
    try {
      // Strategy 1: use the gallery token endpoint (same source as visitor gallery page)
      if (galleryToken) {
        const { data } = await axios.get(`/api/gallery/token/${galleryToken}`);
        const list = (data.photos || []).map(p => ({
          id:               p.id,
          file_name:        p.file_name || '',
          confidence_score: p.confidence_percent != null ? p.confidence_percent / 100 : (p.confidence_score || 0),
          photoUrl:         p.url,
        }));
        if (list.length > 0) {
          setPhotos(list);
          setPhotosSource('gallery');
          setLoadingPhotos(false);
          return;
        }
        // gallery returned 0 (no completed run) — fall through to admin endpoint
      }

      // Strategy 2: admin investigate endpoint (doesn't require completed run)
      if (eumId) {
        const { data } = await axios.get(
          `${API}/investigate/visitors/${eumId}/matched-photos?limit=500`, { headers }
        );
        const photos = (data.photos || []).map(p => ({
          ...p,
          photoUrl: (PHOTO_BASE && p.event_id && p.file_name)
            ? `${PHOTO_BASE}/${p.event_id}/${lowerExt(p.file_name)}`
            : `/api/photos/file/${p.photo_token}`,
        }));
        setPhotos(photos);
        setPhotosSource('admin');
      } else {
        setPhotosError('EUM_ID missing — please restart the Node.js server and reload.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      setPhotosError(`Failed to load photos: ${msg} (EUM_ID: ${eumId ?? 'none'}, token: ${galleryToken ? 'yes' : 'no'})`);
      setPhotos([]);
    }
    setLoadingPhotos(false);
  }, []);

  // ── Cascade handlers ─────────────────────────────────────────────────────────
  const handleDealerChange = id => {
    setDealerId(id); setPhotogId(''); setPhotographers([]); setEventId(''); setEvents([]);
    setVisitors([]); setSelected(null); setForm(null); setRightView(null); setMsg(''); setSearch('');
    if (id) loadPhotographers(id);
  };
  const handlePhotogChange = id => {
    setPhotogId(id); setEventId(''); setEvents([]);
    setVisitors([]); setSelected(null); setForm(null); setRightView(null); setMsg(''); setSearch('');
    if (id) loadEvents(id);
  };
  const handleEventChange = id => {
    setEventId(id); setVisitors([]); setSelected(null); setForm(null); setRightView(null); setMsg(''); setSearch('');
    if (id) loadVisitors(id);
  };

  // ── Visitor selection ────────────────────────────────────────────────────────
  const toForm = o => ({
    surname:        o.Surname        || o.surname        || '',
    name:           o.Name           || o.name           || '',
    mobile_number:  o.MobileNumber   || o.mobile_number  || '',
    email:          o.Email          || o.email          || '',
    password:       '',
    father_husband: o.FatherHusband  || o.father_husband || '',
    dob:            (o.Dob || o.dob) ? String(o.Dob || o.dob).split('T')[0] : '',
    address1:       o.Address1       || o.address1       || '',
    address2:       o.Address2       || o.address2       || '',
    pincode:        o.Pincode        || o.pincode        || '',
    district:       o.District       || o.district       || '',
    state:          o.State          || o.state          || '',
    spounce_name:   o.SpounceName    || o.spounce_name   || '',
    spounce_mobile: o.SpounceMobile  || o.spounce_mobile || '',
    refered_by:     o.ReferedBy      || o.refered_by     || '',
    isd:            o.ISD            || o.isd            || '91',
    is_active:      o.IsActive === 1 || o.is_active === 1 || o.IsActive === true || o.is_active === true,
  });

  const selectVisitor = async (v, view = 'photos') => {
    setSelected(v); setMsg(''); setRightView(view); setCopied(''); setWaResult('');
    setSelfieFile(null); setSelfieMsg('');
    if (view === 'photos' || view === 'edit') {
      try {
        const { data } = await axios.get(`${API}/users/${v.id}`, { headers });
        setForm(toForm(data));
      } catch { setForm(toForm(v)); }
    }
    if (view === 'photos') loadPhotos(v.eum_id, v.gallery_token);
  };

  const switchView = view => {
    setRightView(view);
    if (view === 'photos' && selected) loadPhotos(selected.eum_id, selected.gallery_token);
    if (view === 'edit' && !form && selected) {
      axios.get(`${API}/users/${selected.id}`, { headers })
        .then(r => setForm(toForm(r.data))).catch(() => {});
    }
  };

  const closeRight = () => {
    setRightView(null); setSelected(null); setForm(null); setMsg('');
    setCopied(''); setWaResult(''); setSelfieFile(null); setSelfieMsg('');
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Save visitor ─────────────────────────────────────────────────────────────
  const save = async () => {
    if (!selected) return;
    setSaving(true); setMsg('');
    try {
      const payload = { ...form, role: 'visitor' };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/users/${selected.id}`, payload, { headers });
      setMsg('Saved.');
      loadVisitors(eventId);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Save failed.');
    }
    setSaving(false);
  };

  // ── Selfie upload ────────────────────────────────────────────────────────────
  const uploadSelfie = async () => {
    if (!selfieFile || !selected?.eum_id) return;
    setUploadingSelfie(true); setSelfieMsg('');
    try {
      const fd = new FormData();
      fd.append('selfie', selfieFile);
      await axios.post(
        `${API}/investigate/visitors/${selected.eum_id}/replace-selfie`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      setSelfieMsg('✓ Selfie uploaded — embedding recomputes in ~15 sec');
      setSelfieFile(null);
      setSelfieBust(prev => ({ ...prev, [selected.id]: Date.now() }));
    } catch (err) {
      setSelfieMsg(err.response?.data?.error || 'Upload failed.');
    }
    setUploadingSelfie(false);
  };

  // ── Gallery token generate ───────────────────────────────────────────────────
  const generateGalleryLink = async () => {
    if (!selected?.eum_id) return;
    setGeneratingLink(true);
    try {
      const { data } = await axios.post(
        `${API}/investigate/visitors/${selected.eum_id}/generate-gallery`, {}, { headers }
      );
      const updated = { ...selected, gallery_token: data.gallery_token };
      setSelected(updated);
      setVisitors(prev => prev.map(v => v.id === selected.id ? updated : v));
    } catch {}
    setGeneratingLink(false);
  };

  // ── WhatsApp send ────────────────────────────────────────────────────────────
  const sendWA = async (which, mobileRaw, message) => {
    setWaSending(which); setWaResult('');
    try {
      await sendOnWhatsApp({ mobileRaw, message, token });
      setWaResult('ok');
    } catch {
      setWaResult('fail');
    }
    setWaSending('');
    setTimeout(() => setWaResult(''), 3000);
  };

  // ── Stacked escape ───────────────────────────────────────────────────────────
  escRef.current = () => {
    if (rightView === 'edit')   { switchView('photos'); return; }
    if (rightView === 'photos') { closeRight(); return; }
    if (eventId)  { handleEventChange('');  return; }
    if (photogId) { handlePhotogChange(''); return; }
    if (dealerId) { handleDealerChange(''); return; }
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const filteredVisitors = visitors.filter(v => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (v.Name    || '').toLowerCase().includes(q) ||
      (v.Surname || '').toLowerCase().includes(q) ||
      (v.mobile_number || '').includes(q)
    );
  });

  const visitorDisplayName = selected
    ? `${form?.surname || selected.Surname || ''} ${form?.name || selected.Name || ''}`.trim()
    : '';

  const galleryUrl = selected?.gallery_token
    ? `${SHARE_BASE}/gallery?token=${selected.gallery_token}`
    : null;

  const selectedEvent    = events.find(e => String(e.id) === String(eventId));
  const eventRegisterUrl = eventId
    ? selectedEvent?.register_token
      ? `${SHARE_BASE}/register?token=${selectedEvent.register_token}`
      : `${SHARE_BASE}/register?event_id=${eventId}`
    : null;

  const selCls = 'border border-slate-200 rounded px-2 py-1 text-xs w-full outline-none focus:border-indigo-400';

  const handleCopy = (which, url) => {
    copyText(url);
    setCopied(which);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 48px)' }}>
      <div className="flex flex-col flex-1 overflow-hidden p-3 gap-2">

        {/* ── Filter bar ── */}
        <div className="flex items-end gap-2 bg-white rounded-xl border border-slate-200 px-3 py-2 flex-shrink-0">
          <FilterStep num="1" label="Dealer">
            <select className={selCls} value={dealerId} onChange={e => handleDealerChange(e.target.value)}>
              <option value="">— Select —</option>
              {dealers.map(d => (
                <option key={d.id} value={d.id}>
                  {d.Surname ? `${d.Surname} ${d.Name}` : d.Name} · {d.mobile_number}
                </option>
              ))}
            </select>
          </FilterStep>
          <Arrow />
          <FilterStep num="2" label={`Photographer${loadingP ? ' …' : ''}`}>
            <select className={selCls} value={photogId} onChange={e => handlePhotogChange(e.target.value)} disabled={!dealerId}>
              <option value="">— Select —</option>
              {photographers.map(p => (
                <option key={p.id} value={p.id}>
                  {p.Surname ? `${p.Surname} ${p.Name}` : p.Name} · {p.mobile_number}
                </option>
              ))}
            </select>
          </FilterStep>
          <Arrow />
          <FilterStep num="3" label={`Event${loadingE ? ' …' : ''}`}>
            <select className={selCls} value={eventId} onChange={e => handleEventChange(e.target.value)} disabled={!photogId}>
              <option value="">— Select —</option>
              {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </FilterStep>

          {/* Event-level share */}
          {eventId && (
            <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
              {visitors.length > 0 && (
                <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
                  {visitors.length} visitors
                </span>
              )}
              {eventRegisterUrl && (
                <>
                  <button
                    onClick={() => handleCopy('evturl', eventRegisterUrl)}
                    className={`text-[10px] font-semibold px-2 py-1 rounded-lg border ${copied === 'evturl' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  >
                    {copied === 'evturl' ? '✓ Copied' : '📋 Event URL'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Hint ── */}
        {!eventId && (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
            Select Dealer → Photographer → Event to view visitors
          </div>
        )}

        {/* ── Main content ── */}
        {eventId && (
          <div className="flex flex-1 overflow-hidden gap-2 min-h-0">

            {/* ── Left: visitor list ── */}
            <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
              <div className="px-2 pt-2 pb-1 flex-shrink-0">
                <input
                  className="w-full border border-slate-200 rounded px-2 py-1 text-xs outline-none focus:border-indigo-400"
                  placeholder="Search name or mobile…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              {loadingVis && <p className="text-xs text-slate-400 px-2 py-1">Loading…</p>}
              {!loadingVis && visitors.length === 0 && (
                <p className="text-xs text-slate-400 px-2 py-1">No visitors registered.</p>
              )}
              {!loadingVis && visitors.length > 0 && filteredVisitors.length === 0 && (
                <p className="text-xs text-slate-400 px-2 py-1">No match.</p>
              )}
              <div className="flex-1 overflow-y-auto p-1">
                {filteredVisitors.map((v, i) => {
                  const isActive = selected?.id === v.id;
                  const fullName = `${v.Surname || ''} ${v.Name || ''}`.trim() || '?';
                  return (
                    <div
                      key={v.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border mb-0.5 transition-colors ${isActive ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-transparent hover:bg-slate-50 hover:border-slate-200'}`}
                    >
                      {/* Selfie — click opens Photos */}
                      <div className="cursor-pointer flex-shrink-0" onClick={() => selectVisitor(v, 'photos')}>
                        <SelfieOrAvatar
                          userId={v.id}
                          name={fullName}
                          colorClass={COLORS[i % COLORS.length]}
                          bust={selfieBust[v.id]}
                        />
                      </div>

                      {/* Name + mobile — click opens Photos */}
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => selectVisitor(v, 'photos')}>
                        <div className="font-semibold text-xs text-slate-800 truncate leading-tight">{fullName}</div>
                        <div className="text-[9px] text-slate-500 truncate">{v.mobile_number}</div>
                        {v.match_count > 0 && (
                          <div className="text-[9px] text-green-700 font-semibold">{v.match_count} photos matched</div>
                        )}
                      </div>

                      {/* Edit button (right) */}
                      <button
                        onClick={() => selectVisitor(v, 'edit')}
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 transition-colors ${isActive && rightView === 'edit' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}
                        title="Edit visitor details"
                      >
                        ✏️
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Right: placeholder ── */}
            {!selected && (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm bg-white rounded-xl border border-slate-200">
                <div className="text-center">
                  <div className="text-4xl mb-2">📸</div>
                  <div>Click a visitor to view their photos</div>
                  <div className="text-xs mt-1">or use ✏️ to edit their details</div>
                </div>
              </div>
            )}

            {/* ── Right: visitor detail ── */}
            {selected && (
              <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden min-w-0">

                {/* Header strip */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 flex-shrink-0">
                  <SelfieOrAvatar
                    userId={selected.id}
                    name={visitorDisplayName}
                    colorClass="bg-blue-700"
                    size="w-9 h-9"
                    bust={selfieBust[selected.id]}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-slate-800 truncate">{visitorDisplayName || '—'}</div>
                    <div className="text-[10px] text-slate-500 truncate">{selected.mobile_number}</div>
                  </div>
                  <button
                    onClick={() => switchView('photos')}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors ${rightView === 'photos' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                  >
                    📷 {selected.match_count || 0} Photos
                  </button>
                  <button
                    onClick={() => switchView('edit')}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors ${rightView === 'edit' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={closeRight}
                    className="ml-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500 font-bold text-xs flex-shrink-0"
                    title="Close (Esc)"
                  >
                    ✕
                  </button>
                </div>

                {/* Debug IDs bar */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-1 bg-slate-100 border-b border-slate-200 flex-shrink-0">
                  <IdChip label="DealerID"  value={dealerId}    />
                  <IdChip label="PhotogID"  value={photogId}    />
                  <IdChip label="EventID"   value={eventId}     />
                  <IdChip label="UserID"    value={selected.id} />
                  <IdChip label="EUM_ID"    value={selected.eum_id ?? '⚠ restart server'} warn={!selected.eum_id} />
                </div>

                {/* Share / Gallery bar */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 bg-indigo-50 border-b border-indigo-100 flex-shrink-0">
                  <span className="text-[9px] font-bold text-indigo-600 uppercase tracking-wider flex-shrink-0">Gallery</span>

                  {/* Localhost warning */}
                  {IS_LOCALHOST && (
                    <span className="text-[9px] text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 flex-shrink-0">
                      ⚠ localhost — set REACT_APP_SHARE_BASE in .env.development to your machine IP for external sharing
                    </span>
                  )}

                  {galleryUrl ? (
                    <>
                      <input
                        readOnly
                        value={galleryUrl}
                        className="flex-1 min-w-0 text-[9px] bg-white border border-indigo-200 rounded px-1.5 py-0.5 text-slate-700 outline-none cursor-text"
                        onClick={e => e.target.select()}
                        style={{ minWidth: 120 }}
                      />
                      <button
                        onClick={() => handleCopy('gallery', galleryUrl)}
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 ${copied === 'gallery' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                      >
                        {copied === 'gallery' ? '✓' : '📋'}
                      </button>
                      {/* Send via WA API */}
                      <button
                        onClick={() => sendWA('gallery', selected.mobile_number, buildGalleryMessage({ visitorName: visitorDisplayName, eventName: selectedEvent?.name, galleryUrl }))}
                        disabled={waSending === 'gallery'}
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 transition-colors ${waSending === 'gallery' ? 'bg-green-200 text-green-800 border-green-300' : waResult === 'ok' ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'}`}
                        title="Send gallery link to visitor's mobile via WhatsApp"
                      >
                        {waSending === 'gallery' ? '…' : waResult === 'ok' ? '✓ Sent' : waResult === 'fail' ? '✗ Fail' : '📤 WA'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={generateGalleryLink}
                      disabled={generatingLink || !selected.eum_id}
                      className="text-[9px] font-bold px-2 py-0.5 rounded border bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50 disabled:opacity-50"
                    >
                      {generatingLink ? '…' : '+ Generate Link'}
                    </button>
                  )}

                  <span className="text-slate-300 flex-shrink-0">|</span>
                  <span className="text-[9px] font-bold text-slate-500 flex-shrink-0">Event Reg:</span>
                  {eventRegisterUrl && (
                    <>
                      <button
                        onClick={() => handleCopy('evtreg', eventRegisterUrl)}
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 ${copied === 'evtreg' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                      >
                        {copied === 'evtreg' ? '✓' : '📋'}
                      </button>
                    </>
                  )}
                </div>

                {/* ── Photos view ── */}
                {rightView === 'photos' && (
                  <div className="flex-1 overflow-y-auto p-3">

                    {/* Loading */}
                    {loadingPhotos && (
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                        <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                        Loading photos…
                      </div>
                    )}

                    {/* Error */}
                    {!loadingPhotos && photosError && (
                      <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
                        ❌ {photosError}
                        <button
                          onClick={() => loadPhotos(selected.eum_id, selected.gallery_token)}
                          className="ml-2 underline font-bold"
                        >Retry</button>
                      </div>
                    )}

                    {/* No photos (no error) */}
                    {!loadingPhotos && !photosError && photos.length === 0 && (
                      <div className="text-center py-8">
                        <div className="text-3xl mb-2">🔍</div>
                        <p className="text-xs text-slate-500 mb-1">No matched photos found.</p>
                        <p className="text-[10px] text-slate-400 mb-3">
                          Face matching may not have run yet, or no faces matched for this visitor.
                        </p>
                        <button
                          onClick={() => loadPhotos(selected.eum_id, selected.gallery_token)}
                          className="text-xs font-bold px-3 py-1 rounded-lg border border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                        >
                          🔄 Retry Load
                        </button>
                      </div>
                    )}

                    {/* ── Gallery share card ── */}
                    {galleryUrl && (
                      <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 mb-3">
                        <p className="text-[10px] font-bold text-green-800 mb-1.5 uppercase tracking-wide">
                          📲 Share Gallery with {visitorDisplayName || 'Visitor'}
                        </p>
                        {/* URL row */}
                        <div className="flex items-center gap-1.5 mb-2">
                          <input
                            readOnly
                            value={galleryUrl}
                            className="flex-1 min-w-0 text-[10px] bg-white border border-green-200 rounded-lg px-2 py-1 text-slate-700 outline-none cursor-text"
                            onClick={e => e.target.select()}
                          />
                          <button
                            onClick={() => handleCopy('gallery2', galleryUrl)}
                            className={`text-[10px] font-bold px-2 py-1 rounded-lg border flex-shrink-0 transition-colors ${copied === 'gallery2' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                          >
                            {copied === 'gallery2' ? '✓ Copied' : '📋 Copy'}
                          </button>
                        </div>
                        {/* WhatsApp buttons */}
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => sendWA('gallery', selected.mobile_number, buildGalleryMessage({ visitorName: visitorDisplayName, eventName: selectedEvent?.name, galleryUrl }))}
                            disabled={waSending === 'gallery'}
                            className={`flex-1 text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors ${waSending === 'gallery' ? 'bg-green-200 text-green-800 border-green-300' : waResult === 'ok' ? 'bg-green-600 text-white border-green-600' : 'bg-green-600 text-white border-green-600 hover:bg-green-700'}`}
                          >
                            {waSending === 'gallery' ? '⏳ Sending…' : waResult === 'ok' ? '✅ Sent!' : waResult === 'fail' ? '❌ Failed' : '📤 Send on WhatsApp'}
                          </button>
                        </div>
                        {IS_LOCALHOST && (
                          <p className="text-[9px] text-amber-600 mt-1.5">
                            ⚠ URL uses localhost — set REACT_APP_SHARE_BASE in .env.development to your machine IP for external sharing
                          </p>
                        )}
                      </div>
                    )}

                    {/* Photos count + source */}
                    {photos.length > 0 && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold text-slate-700">{photos.length} photos</span>
                        <span className="text-[9px] text-slate-400">
                          via {photosSource === 'gallery' ? 'gallery token ✓' : 'admin endpoint'}
                        </span>
                        <button
                          onClick={() => loadPhotos(selected.eum_id, selected.gallery_token)}
                          className="text-[9px] text-indigo-500 hover:text-indigo-700 ml-auto"
                        >🔄 Reload</button>
                      </div>
                    )}

                    {/* Photo grid — 4 cols, click opens full size */}
                    <div className="grid grid-cols-4 gap-2">
                      {photos.map((p, idx) => (
                        <div
                          key={p.id}
                          className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-100 cursor-pointer hover:scale-105 hover:border-indigo-400 transition-all shadow-sm"
                          style={{ aspectRatio: '1' }}
                          onClick={() => window.open(p.photoUrl, '_blank')}
                          title={`Photo ${idx + 1} — ${Math.round((p.confidence_score || 0) * 100)}% match`}
                        >
                          <img
                            src={p.photoUrl}
                            alt={p.file_name || `photo-${p.id}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={e => {
                              e.target.style.opacity = '0.15';
                              e.target.title = 'File not found on disk';
                            }}
                          />
                          {p.confidence_score > 0 && (
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[8px] text-center py-0.5 font-bold">
                              {Math.round(p.confidence_score * 100)}%
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Edit view ── */}
                {rightView === 'edit' && form && (
                  <>
                    <div className="flex-1 overflow-y-auto p-3">

                      {/* Selfie section */}
                      <div className="flex items-center gap-3 mb-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                        <SelfieOrAvatar
                          userId={selected.id}
                          name={visitorDisplayName}
                          colorClass="bg-blue-700"
                          size="w-14 h-14"
                          bust={selfieBust[selected.id]}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">Selfie</div>
                          <input
                            type="file"
                            accept="image/*"
                            ref={selfieRef}
                            className="hidden"
                            onChange={e => { setSelfieFile(e.target.files[0] || null); setSelfieMsg(''); }}
                          />
                          {selfieFile ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] text-slate-600 truncate max-w-[120px]">{selfieFile.name}</span>
                              <button
                                onClick={uploadSelfie}
                                disabled={uploadingSelfie || !selected.eum_id}
                                className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                              >
                                {uploadingSelfie ? 'Uploading…' : 'Upload'}
                              </button>
                              <button
                                onClick={() => setSelfieFile(null)}
                                className="text-[10px] text-slate-500 hover:text-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => selfieRef.current?.click()}
                              className="text-[10px] font-bold px-2 py-0.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                            >
                              📷 Change Selfie
                            </button>
                          )}
                          {selfieMsg && (
                            <div className={`text-[10px] mt-1 ${selfieMsg.startsWith('✓') ? 'text-green-700' : 'text-red-600'}`}>
                              {selfieMsg}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Form fields */}
                      <div className="grid grid-cols-6 gap-x-3 gap-y-2">
                        {FIELDS.map(([label, key, type = 'text']) => (
                          <div key={key} className="flex flex-col gap-0.5">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{label}</label>
                            <input
                              className="border border-slate-200 rounded px-2 py-1 text-xs outline-none focus:border-indigo-400 w-full"
                              type={type}
                              placeholder={key === 'password' ? 'keep blank' : ''}
                              value={form[key] || ''}
                              onChange={e => f(key, e.target.value)}
                            />
                          </div>
                        ))}
                        <div className="col-span-2 flex items-end pb-1">
                          <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                            <input type="checkbox" checked={!!form.is_active} onChange={e => f('is_active', e.target.checked)} className="w-3.5 h-3.5" />
                            Active
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Save bar */}
                    <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex-shrink-0 flex items-center gap-3">
                      <button
                        ref={saveRef}
                        onClick={save}
                        disabled={saving}
                        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-bold px-5 py-1.5 rounded-lg"
                      >
                        {saving ? 'Saving…' : 'Save Changes'}
                      </button>
                      {msg && (
                        <div className={`text-xs font-semibold px-3 py-1 rounded-lg border ${msg === 'Saved.' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                          {msg}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminVisitorsMgmtPage;
